import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import PageMeta from "../../components/seo/PageMeta";
import SocialLinks from "../../components/SocialLinks";
import { FEATURES } from "../../data/features";

const SITE = "https://academicexaminer.online";

function MyAELogo({ size = 30 }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="rounded-lg bg-indigo-600 flex items-center justify-center font-mono font-bold text-white shadow-sm" style={{ width: size, height: size, fontSize: size * 0.48 }}>Æ</div>
      <span className="font-display font-semibold text-zinc-900 tracking-tight" style={{ fontSize: size * 0.55 }}>My<span className="text-indigo-600">AE</span></span>
    </div>
  );
}

export default function FeaturesHub() {
  const navigate = useNavigate();
  const canonical = `${SITE}/features`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "MyAE", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Features", item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "MyAE Features",
      description:
        "Every MyAE feature explained — Grade Prediction, Assignment Analysis, Essay Feedback, Submission Intelligence and Assignment Brief Analysis.",
      url: canonical,
      isPartOf: { "@id": `${SITE}/#website` },
      about: { "@id": `${SITE}/#software` },
      inLanguage: "en-GB",
      hasPart: FEATURES.map((f) => ({
        "@type": "WebPage",
        name: f.title,
        url: `${SITE}/features/${f.slug}`,
        description: f.intro,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900" data-testid="features-hub">
      <PageMeta
        title="Features — MyAE"
        description="Every MyAE feature explained — Grade Prediction, Assignment Analysis, Essay Feedback, Submission Intelligence and Assignment Brief Analysis. Calm, rubric-driven, student-focused."
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="cursor-pointer"><MyAELogo /></button>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/pricing" className="text-zinc-600 hover:text-zinc-900">Pricing</Link>
            <button onClick={() => navigate("/new")} className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700" data-testid="features-hub-cta-header">Try MyAE</button>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <nav className="text-xs text-zinc-500 flex gap-x-4" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-zinc-900">MyAE</Link>
          <ChevronRight size={12} className="mt-0.5" />
          <span className="text-zinc-900">Features</span>
        </nav>

        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.05]" data-testid="features-hub-title">
          Every MyAE feature, explained.
        </h1>
        <p className="mt-4 text-lg text-zinc-600 max-w-2xl leading-relaxed">
          MyAE is your AI-assisted study companion for university assignments. Each feature below is a
          separate lens on your work — together, they give you a rubric-aware view of your draft
          before you submit.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {FEATURES.map((f) => (
            <Link key={f.slug} to={`/features/${f.slug}`} className="group block rounded-2xl border border-zinc-200 hover:border-indigo-300 bg-white p-6 transition-colors" data-testid={`features-hub-card-${f.slug}`}>
              <p className="text-xs uppercase tracking-widest text-indigo-500">Feature</p>
              <h2 className="mt-1 font-display text-2xl text-zinc-900 group-hover:text-indigo-700 transition-colors">{f.title}</h2>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{f.intro}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 group-hover:gap-2 transition-all">
                Learn more <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-6 sm:p-8" data-testid="features-hub-cta">
          <h3 className="font-display text-2xl tracking-tight">Ready to try MyAE?</h3>
          <p className="mt-2 text-zinc-600">Upload your brief and current draft. It takes about a minute. Free until further notice.</p>
          <button onClick={() => navigate("/new")} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-indigo-700">
            Analyse an assignment <ArrowRight size={15} />
          </button>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white mt-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <MyAELogo size={22} />
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/features" className="hover:text-zinc-900">Features</Link>
            <Link to="/pricing" className="hover:text-zinc-900">Pricing</Link>
            <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
            <Link to="/contact" className="hover:text-zinc-900">Contact</Link>
          </div>
          <SocialLinks className="basis-full sm:basis-auto" />
          <p className="text-zinc-400 basis-full sm:basis-auto">© {new Date().getFullYear()} OpenDegree Ltd</p>
        </div>
      </footer>
    </div>
  );
}
